import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateField } from "@/components/common/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { downloadPdf, printHtml } from "@/lib/printDoc";
import { money, shortDate } from "@/lib/format";

const statementHtml = (s) => {
  const cur = s.currency;
  return `
  <h1>${s.organisation.name}</h1>
  ${s.organisation.trading_name ? `<div class="muted">${s.organisation.trading_name}</div>` : ""}
  <div class="rule"></div>
  <h2>Statement of hall hire account</h2>
  <div class="meta">
    <div><span>Hirer</span>${s.hirer.name}</div>
    <div><span>Contact</span>${s.hirer.contact || "—"}</div>
    <div><span>Period</span>${s.period.from} to ${s.period.to}</div>
  </div>
  <table><thead><tr><th>Date</th><th>Reference</th><th>Description</th>
    <th class="r">Charged</th><th class="r">Received</th><th class="r">Deposit</th></tr></thead>
  <tbody>${s.lines.map((l) => `<tr><td>${l.date}</td><td>${l.reference}</td>
    <td>${l.description}</td>
    <td class="r">${l.charged ? `${cur} ${l.charged.toFixed(2)}` : "—"}</td>
    <td class="r">${l.paid ? `${cur} ${l.paid.toFixed(2)}` : "—"}</td>
    <td class="r">${l.deposit ? `${cur} ${l.deposit.toFixed(2)}` : "—"}</td></tr>`).join("")}</tbody>
  <tfoot>
    <tr><td></td><td></td><td class="r">Totals</td>
      <td class="r">${cur} ${s.charged.toFixed(2)}</td>
      <td class="r">${cur} ${s.paid.toFixed(2)}</td>
      <td class="r">${cur} ${s.deposit_held.toFixed(2)}</td></tr>
    <tr><td></td><td></td><td class="r">Outstanding</td><td></td>
      <td class="r">${cur} ${s.outstanding.toFixed(2)}</td><td></td></tr>
  </tfoot></table>
  <p class="muted">Amounts shown as charged become payable on the date stated on each hire invoice.</p>`;
};

const monthsAway = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

export const Hirers = () => {
  const [data, setData] = useState(null);
  const [statement, setStatement] = useState(null);
  const [target, setTarget] = useState(null);  const [range, setRange] = useState({ from: monthsAway(-12), to: monthsAway(12) });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/property/hirers");
    setData(res.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const fetchStatement = async (name) => {
    setBusy("fetch");
    try {
      const res = await api.get("/property/hirers/statement",
        { params: { hirer: name, date_from: range.from, date_to: range.to } });
      setStatement(res.data);
    } catch (e) { toast.error(apiError(e)); setStatement(null); }
    setBusy("");
  };

  const openStatement = async (row) => {
    setTarget(row);
    setStatement(null);
    await fetchStatement(row.name);
  };

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="hirers-directory">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="kpi-hirers" label="Hirers" value={data.counters.hirers} />
        <Kpi testId="kpi-hirer-charged" label="Charged" accent="violet"
             value={money(data.counters.charged, "INR", true)} />
        <Kpi testId="kpi-hirer-collected" label="Received" accent="emerald"
             value={money(data.counters.collected, "INR", true)} />
        <Kpi testId="kpi-hirer-outstanding" label="Outstanding" accent="amber"
             value={money(data.counters.outstanding, "INR", true)} />
      </div>

      <Panel testId="panel-hirers" kicker="Who hires from us" title="Hirer directory">
        <Table testId="hirers-table"
               head={<><Th>Hirer</Th><Th>Contact</Th><Th>Bookings</Th><Th>Hours</Th><Th>Charged</Th>
                 <Th>Invoiced</Th><Th>Received</Th><Th>Deposit held</Th><Th>Outstanding</Th>
                 <Th>Last booking</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={11} label="No chargeable hirers yet" />}
          {data.items.map((h) => (
            <tr key={h.key} className="row-hover" data-testid={`hirer-row-${h.key}`}>
              <Td className="text-[11.5px] font-medium text-foreground">{h.name}
                {h.cancelled > 0 && (
                  <div className="text-[10px] text-muted-foreground">{h.cancelled} cancelled</div>
                )}</Td>
              <Td className="font-mono text-[10.5px]">{h.contact || "—"}</Td>
              <Td className="font-mono text-[11px]">{h.bookings}</Td>
              <Td className="font-mono text-[11px]">{h.hours}</Td>
              <Td className="font-mono text-[11px]">{money(h.charged, h.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(h.invoiced, h.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(h.paid, h.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(h.deposit_held, h.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(h.outstanding, h.currency)}</Td>
              <Td className="font-mono text-[10.5px]">{shortDate(h.last_booking)}</Td>
              <Td>
                <Button size="sm" variant="outline" data-testid={`hirer-statement-${h.key}`}
                        onClick={() => openStatement(h)}
                        className="h-7 rounded-full border-border px-2.5 text-[10px]">
                  Statement</Button>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Dialog open={Boolean(target)} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-3xl border-border bg-card" data-testid="statement-dialog">
          <DialogHeader><DialogTitle>{target?.name} · hire account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">From</Label>
                <DateField value={range.from} testId="statement-from-input"
                           onChange={(v) => setRange({ ...range, from: v })}
                           clearable={false} className="h-8 w-[150px] text-[11.5px]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">To</Label>
                <DateField value={range.to} testId="statement-to-input"
                           onChange={(v) => setRange({ ...range, to: v })}
                           clearable={false} className="h-8 w-[150px] text-[11.5px]" />
              </div>
              <Button size="sm" data-testid="statement-refresh" disabled={busy === "fetch"}
                      onClick={() => fetchStatement(target.name)}
                      className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "fetch" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Show
              </Button>
              {statement && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" data-testid="statement-print"
                          onClick={() => printHtml(`Statement · ${statement.hirer.name}`,
                            statementHtml(statement))}
                          className="h-8 rounded-full border-border px-3 text-[10.5px]">
                    <Printer className="mr-1 h-3 w-3" /> Print</Button>
                  <Button size="sm" variant="outline" data-testid="statement-pdf"
                          onClick={() => downloadPdf("/property/hirers/statement/pdf",
                            { hirer: target.name, date_from: range.from, date_to: range.to },
                            `hire-statement-${target.key.replace(/[^a-z0-9]+/gi, "-")}.pdf`).catch((e) => toast.error(apiError(e)))}
                          className="h-8 rounded-full border-border px-3 text-[10.5px]">
                    <Download className="mr-1 h-3 w-3" /> PDF</Button>
                </div>
              )}
            </div>

            {statement && (
              <>
                <div className="grid grid-cols-4 gap-3">
                  {[["Charged", statement.charged], ["Received", statement.paid],
                    ["Outstanding", statement.outstanding],
                    ["Deposit held", statement.deposit_held]].map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-border bg-muted/25 p-3">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                      <div className="text-[14px] font-bold text-foreground">
                        {money(v, statement.currency)}</div>
                    </div>
                  ))}
                </div>
                <div className="max-h-[45vh] overflow-y-auto">
                  <Table testId="statement-table"
                         head={<><Th>Date</Th><Th>Reference</Th><Th>Description</Th><Th>Charged</Th>
                           <Th>Received</Th><Th>Deposit</Th></>}>
                    {statement.lines.length === 0 && <Empty colSpan={6} label="Nothing in this period" />}
                    {statement.lines.map((l, i) => (
                      <tr key={`${l.reference}-${i}`} data-testid={`statement-row-${i}`}>
                        <Td className="font-mono text-[11px]">{l.date}</Td>
                        <Td className="font-mono text-[10.5px]">{l.reference}</Td>
                        <Td className="text-[11.5px]">{l.description}</Td>
                        <Td className="font-mono text-[11px]">
                          {l.charged ? money(l.charged, statement.currency) : "—"}</Td>
                        <Td className="font-mono text-[11px]">
                          {l.paid ? money(l.paid, statement.currency) : "—"}</Td>
                        <Td className="font-mono text-[11px]">
                          {l.deposit ? money(l.deposit, statement.currency) : "—"}</Td>
                      </tr>
                    ))}
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
